import React, { useEffect, useMemo, useState } from 'react';
import { Field } from 'formik';
import moment from 'moment';

import { Button, Checkbox, IconButton, Radio, TextField } from '@material-ui/core';

import ViewIcon from '@material-ui/icons/Visibility';
import DetailsIcon from '@material-ui/icons/DescriptionOutlined';

import { currencyFormater } from '../utils/format-utils';
import helpers from 'app/utils/helpers';
import { FORMATTED } from '../constants';
import DocumentField from 'app/components/DocumentField';
import CustomMaskField from './MaskField';

function formatValue(value, format) {
  switch (format) {
    case FORMATTED.date:
      return moment(value).format('DD/MM/YYYY');
    case FORMATTED.currency:
      return currencyFormater(value);
    case FORMATTED.document:
      return helpers.formatarDocumento(value);
    default:
      return value;
  }
}

export const OfActive = ({ data, field, invertIsActive }) => {
  const isActive = () => {
    return Boolean(data[field]);
  };

  return (
    Boolean(data) && (
      <Checkbox
        disabled
        checked={invertIsActive ? !isActive() : isActive()}
        style={{ cursor: 'default' }}
        color="default"
      />
    )
  );
};

export const OfPendencies = ({ data, field, onOpenDetails, otherPendenciesField }) => {
  const hasPendency = Boolean(data[field]?.trim() || data[otherPendenciesField]?.length > 0);

  return (
    <Button
      endIcon={hasPendency ? <ViewIcon /> : null}
      disabled={!hasPendency}
      size="small"
      variant="text"
      color="secondary"
      onClick={e => onOpenDetails(e, data)}
    >
      {hasPendency ? 'Sim' : 'Não'}
    </Button>
  );
};

export const OfDetails = ({ data, field, onOpenDetails }) => {
  const hasDetails = Boolean(data[field]?.trim());

  return (
    <IconButton onClick={e => onOpenDetails(e, data)} disabled={!hasDetails} color="primary">
      <DetailsIcon />
    </IconButton>
  );
};

export const OfSelect = ({ data, field, onChangeSelect }) => {
  const [checked, setChecked] = useState(false);

  React.useEffect(() => {
    setChecked(() => Boolean(data[field]));
  }, [data, field]);

  const handleSelect = () => {
    if (!onChangeSelect) return;
    // setChecked(prev => !prev);

    if (checked) onChangeSelect({ action: 'remove', data });
    if (!checked) onChangeSelect({ action: 'add', data });
  };

  return <Checkbox checked={checked} onChange={handleSelect} color="default" />;
};

export const OfRadio = ({ data, field, selectedId, onChangeSelect }) => {
  const [internalSelected, setSelected] = useState(null);
  const handleSelect = event => {
    setSelected(event.target.value);
    onChangeSelect({ action: 'select', data });
  };
  return (
    <Radio
      name={field}
      value={data[field]}
      checked={selectedId ? selectedId === data[field] : internalSelected === data[field]}
      onChange={handleSelect}
      color="default"
    />
  );
};

export const OfRadioV2 = ({ data, field, selectedId, onChangeSelect }) => {
  const [internalSelected, setSelected] = useState(null);
  const handleSelect = event => {
    setSelected(event.target.value);
    onChangeSelect({ action: 'select', data });
  };
  return (
    <Radio
      name={field}
      value={data[field]}
      checked={
        data.checked
          ? data.checked
          : selectedId
          ? selectedId === data[field]
          : internalSelected === data[field]
      }
      disabled={data?.lockCheckbox}
      onChange={handleSelect}
      color="default"
    />
  );
};

export const OfCheckBox = ({ data, field, onChangeSelect, index }) => {
  const handleSelect = () => {
    onChangeSelect({ action: 'check', data, index });
  };
  return (
    <Checkbox
      name={field}
      value={data[field]}
      checked={data.checked ? data.checked : false}
      onChange={handleSelect}
      color="default"
      disabled={data?.lockCheckbox}
    />
  );
};

export const OfTextField = ({ data, field, onChangeTextField, ...rest }) => {
  const errorMessage = useMemo(() => {
    if(data.error && data.error[field]){
      return data.error[field];
    }
    return '';
  },[data, field]);

  const inputProps = useMemo(() => {
    if(rest.inputProps && rest.inputProps[field]){
      return rest.inputProps[field];
    }
    return {};
  },[rest, field]);

  const handleChange = event => {
    if(onChangeTextField)
      if(onChangeTextField) onChangeTextField({name: event.target.name, value: event.target.value, index: rest.index});
  };
  return (
    <TextField
      name={field}
      value={data[field]}
      variant={rest.variant || 'filled'}
      margin={rest.margin || 'dense'}
      onChange={handleChange}
      error={errorMessage !== ''}
      helperText={errorMessage}
      inputProps={inputProps}
    />
  );
};

export const OfRequestNumberInput = ({ data, field, onChangeTextField, onBlurRequest, setOpenRequestDrawer, ...rest }) => {
  const [loading, setLoading] = useState(false);

  const errorMessage = useMemo(() => {
    if(data.error && data.error[field]){
      return data.error[field];
    }
    return '';
  },[data, field]);

  const handleChange = event => {
    if(onChangeTextField) onChangeTextField({name: event.target.name, value: event.target.value, index: rest.index});
  };

  const handleBlur = async event => {
    if(onBlurRequest) {
      setLoading(true);
      await onBlurRequest({ value: event.target.value, index: rest.index });
      setLoading(false);
    }
  }

  return (
    <CustomMaskField
      mask={'999999/9999'}
      name={field}
      value={data[field]}
      loading={loading}
      variant={rest.variant || 'filled'}
      margin={rest.margin || 'dense'}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={data?.disabled || false}
      error={errorMessage !== ''}
      helperText={errorMessage}
      onClickButton={() => {
        setOpenRequestDrawer({ open: true, index: rest.index })
      }}
    />
  );
};

export const OfSelectDocumentInput = props => {
  const { data, index, documentList = [], onSelectDocType, uploadingState, ...rest } = props;

  const [mergedList, setMergedList] = useState([]);

  const errorMessage = useMemo(() => {
    if(data.error && data.error.tipoDocumento){
      return data.error.tipoDocumento;
    }
    return '';
  },[data]);

  useEffect(() => {
    setMergedList(() => [
      ...documentList,
      { cdTipoDocumento: 999, nmTipoDocumento: 'Documentos adicionais' },
    ]);
  }, [documentList]);

  const handleSelect = cdTipo => {
    let found = mergedList.find(doc => doc.cdTipoDocumento === Number(cdTipo));
    if (onSelectDocType) {
      if (!found) {
        found = null;
      }
      onSelectDocType(found, index);
    }
  };

  return (
    <TextField
      variant={rest.variant || 'filled'}
      margin={rest.margin || 'dense'}
      label="Arquivo"
      fullWidth
      select
      disabled={data?.lockSelect || uploadingState}
      onChange={e => handleSelect(e.target.value)}
      value={data?.tipoDocumento?.cdTipoDocumento || ''}
      SelectProps={{
        native: true,
      }}
      InputLabelProps={{ shrink: true }}
      error={errorMessage !== ''}
      helperText={errorMessage}
    >
      <option>Selecione...</option>
      {mergedList?.map(document => {
        return (
          <option key={document.cdTipoDocumento} value={document['cdTipoDocumento']}>
            {document['nmTipoDocumento']}
          </option>
        );
      })}
    </TextField>
  );
};

export const OfDocumentField = props => {
  const { data, index, onSelectFile, onDeleteFile, setUploadingState, uploadingState, idProperty, idValue } = props;
  if (!idProperty) {
    return <p>Missing [idProperty]</p>;
  }

  const cdTipoDocumento = data?.tipoDocumento?.cdTipoDocumento || 999;
  const nomeDocumento = data?.tipoDocumento?.nmTipoDocumento || `indefinido-${data?.id || index}`;

  const handleUploadFile = docResult => {
    setUploadingState(false);
    onSelectFile(
      {
        [idProperty]: idValue,
        cdTipoDocumento: cdTipoDocumento,
        idFile: docResult.id,
        deDescricao: docResult.originalFileName,
        fileName: docResult.originalFileName,
        tipoDocumento: data?.tipoDocumento,
        canEdit: true,
        boExcluido: false,
        boAlterado: true,
        lockSelect: true,
      },
      index
    );
  };

  const handleDeleteFile = docId => {
    setUploadingState(false);
    onDeleteFile(docId, index);
  };

  return (
    <Field
      component={DocumentField}
      fullWidth
      variant="filled"
      margin="dense"
      disabled={!data?.canEdit}
      id={`attc-${data?.id || index}`}
      value={data?.fileName || ''}
      InputLabelProps={{ shrink: true }}
      idFile={data?.idFile || null}
      idGdoc={data?.idGdoc || null}
      field={{ name: nomeDocumento }}
      onDeleteFile={handleDeleteFile}
      onChangeFile={handleUploadFile}
      uploading={uploadingState}
      setUpLoading={setUploadingState}
    />
  );
};

export const OfDate = ({ data, field }) => {
  let formattedDate = moment(data[field]).format('DD/MM/YYYY');
  if (field.indexOf('.') >= 0) {
    const splittedField = field.split('.');
    const value = splittedField.length > 1 ? data[splittedField[0]][splittedField[1]] : data[field];
    formattedDate = moment(value).format('DD/MM/YYYY');
  }
  return <>{formattedDate}</>;
};

// o componente OfDate retorna a data atual caso o campo esteja nullish, de acordo com a biblioteca 'moment'.
// em algumas telas é interessante que não apareça quando é nullish, por isso este componente.
export const OfOptionalDate = ({ data, field }) => {
  const splitField = field?.split('.') || [];
  const nestedValue = splitField?.length === 2; // não gosto muito dessa ideia, mas fizeram assim...
  const value = nestedValue ? data[splitField[0]][splitField[1]] : data[splitField[0]];

  if (!data || !field || !value) {
    return <></>;
  }

  return <>{moment(value).format('DD/MM/YYYY')}</>;
};

export const OfCurrency = ({ data, field }) => {
  const formattedCurrency = currencyFormater(data[field]);

  return <>{formattedCurrency}</>;
};

export const OfDocumentNumber = ({ data, field }) => {
  let formattedDocument = '';
  if(data && data[field]){
    formattedDocument = helpers.formatarDocumento(data[field]);
  }
  return <>{formattedDocument}</>;
};

export const OfNestedObject = ({ data, field, container, format }) => {
  const hasContainerProperty = Boolean(container);
  const value = hasContainerProperty ? data[container][field] : data[field];

  return <>{formatValue(value, format)}</>;
};

export const OfFormatted = ({ data, field, format }) => {
  return <>{formatValue(data[field], format)}</>;
};

export const OfNestedObjectV2 = ({ data, field, format }) => {
  const splittedField = field.split('.');
  const value = splittedField.length > 1 ? data[splittedField[0]][splittedField[1]] : data[field];

  return <>{formatValue(value, format)}</>;
};
